import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, CircularProgress, ToggleButtonGroup, ToggleButton, Button, Tooltip, Rating
} from '@mui/material'
import { FC, useState, useEffect } from 'react'
import { Api, numberWithCommas } from '../../helpers'
import { Connect, mapStateToProps } from '../../Redux'

type OrderStatus = 'PendingPayment' | 'Paid' | 'Installed' | 'Completed' | 'Cancelled'
type FilterStatus = 'All' | OrderStatus

interface GadgetOrder {
    _id: string
    buyerUsername?: string
    buyerPhone?: string
    quantity: number
    amountKES: number
    depositKES?: number
    interestKES?: number
    balanceKES?: number
    balanceRecoveredKES?: number
    currency: string
    displayAmount: number
    country?: string
    phone: string
    address: string
    notes?: string
    status: OrderStatus
    createdAt: string
    review?: { rating: number, comment?: string, reviewedAt: string }
}

interface StateProps {
    orders: GadgetOrder[]
    loading: boolean
    filter: FilterStatus
    updatingId: string | null
}

const statusColor: Record<OrderStatus, { bg: string; color: string }> = {
    PendingPayment: { bg: '#fef9c3', color: '#854d0e' },
    Paid: { bg: '#dbeafe', color: '#1e40af' },
    Installed: { bg: '#e0e7ff', color: '#3730a3' },
    Completed: { bg: '#dcfce7', color: '#166534' },
    Cancelled: { bg: '#fee2e2', color: '#991b1b' }
}
const statusLabel: Record<OrderStatus, string> = {
    PendingPayment: 'Awaiting Payment',
    Paid: 'Paid',
    Installed: 'Installed',
    Completed: 'Completed',
    Cancelled: 'Cancelled'
}

const filters: FilterStatus[] = ['All', 'PendingPayment', 'Paid', 'Installed', 'Completed', 'Cancelled']
const headers = ['Date', 'Buyer', 'Qty', 'Amount', 'Balance', 'Install Phone', 'Address', 'Status', 'Review', 'Actions']

const GadgetOrders: FC = () => {
    const [state, setState] = useState<StateProps>({
        orders: [],
        loading: true,
        filter: 'All',
        updatingId: null
    })
    const { orders, loading, filter, updatingId } = state

    const getOrders = (status: FilterStatus = 'All') => {
        setState(s => ({ ...s, loading: true }))
        Api({
            controller: 'GadgetOrders',
            action: 'getGadgetOrders',
            ...(status !== 'All' && { status })
        })
            .then((resp: any) => setState(s => ({ ...s, orders: resp || [], loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const handleFilter = (_: any, val: FilterStatus | null) => {
        if (!val) return
        setState(s => ({ ...s, filter: val }))
        getOrders(val)
    }

    const markInstalled = (orderId: string) => {
        setState(s => ({ ...s, updatingId: orderId }))
        Api({ controller: 'GadgetOrders', action: 'markGadgetOrderInstalled', orderId })
            .then(() => getOrders(filter))
            .finally(() => setState(s => ({ ...s, updatingId: null })))
    }

    useEffect(() => { getOrders() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Gadget Orders</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    Customers who've ordered a pool table gadget for installation.
                </Typography>
            </Box>

            <Box sx={{ px: '5px', mb: 2 }}>
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={handleFilter}
                    size='small'
                    sx={{ bgcolor: '#fff', borderRadius: 2, flexWrap: 'wrap' }}
                >
                    {filters.map(f => (
                        <ToggleButton
                            key={f}
                            value={f}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 2.5,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }
                            }}
                        >
                            {f === 'All' ? 'All' : statusLabel[f as OrderStatus]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ mx: '5px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                {headers.map(h => (
                                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                        No {filter === 'All' ? '' : statusLabel[filter as OrderStatus].toLowerCase()} orders found.
                                    </TableCell>
                                </TableRow>
                            ) : orders.map((order) => (
                                <TableRow key={order._id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.85rem' }}>
                                        {new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {order.buyerUsername || '-'}
                                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>{order.buyerPhone}</Typography>
                                    </TableCell>
                                    <TableCell>{numberWithCommas(order.quantity)}</TableCell>
                                    <TableCell>
                                        {order.currency} {numberWithCommas(order.displayAmount)}
                                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>KES {numberWithCommas(order.amountKES)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {order.balanceKES ? (
                                            <>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#b45309' }}>
                                                    KES {numberWithCommas(order.balanceKES)} owed
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                    Deposit KES {numberWithCommas(order.depositKES || 0)}
                                                    {order.interestKES ? ` · Interest KES ${numberWithCommas(order.interestKES)}` : ''}
                                                    {order.balanceRecoveredKES ? ` · Recovered KES ${numberWithCommas(order.balanceRecoveredKES)}` : ''}
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography sx={{ fontSize: '0.8rem', color: '#166534', fontWeight: 600 }}>Paid in full</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{order.phone}</TableCell>
                                    <TableCell sx={{ maxWidth: 220 }}>
                                        {order.address}
                                        {order.country && (
                                            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>{order.country}</Typography>
                                        )}
                                        {order.notes && (
                                            <Tooltip title={order.notes}>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', cursor: 'help' }}>
                                                    Note attached
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={statusLabel[order.status]}
                                            size='small'
                                            sx={{
                                                bgcolor: statusColor[order.status].bg,
                                                color: statusColor[order.status].color,
                                                fontWeight: 600,
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {order.review ? (
                                            <Tooltip title={order.review.comment || 'No comment left'}>
                                                <Box sx={{ cursor: order.review.comment ? 'help' : 'default' }}>
                                                    <Rating value={order.review.rating} readOnly size='small' />
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {order.status === 'Paid' && (
                                            <Button
                                                size='small'
                                                variant='contained'
                                                disabled={updatingId === order._id}
                                                onClick={() => markInstalled(order._id)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                {updatingId === order._id ? 'Saving...' : 'Mark Installed'}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    )
}

export default Connect(mapStateToProps)(GadgetOrders)
